import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { toast } from "sonner";
import { CheckCircle2, Eye, FileDown, Loader2, Mail, Plus, Printer, Search } from "lucide-react";
import {
  getMyAccess,
  getSickNote,
  issueSickNote,
  listPractitioners,
  searchSickNotePatients,
} from "@/lib/sick-notes.functions";
import { CONDITION_NATURES, leaveDays, suggestedReturnDate, validateSickNoteDates, type SickNote, type LeaveType, type ConditionNature } from "@/lib/sick-notes";
import { PRACTICE_CREDENTIALS as P, formatInvoiceDate } from "@/lib/invoice-details";
import { SickNoteDocument } from "@/components/admin/SickNoteDocument";
import { useSickNoteActions } from "@/components/admin/useSickNoteActions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export const Route = createFileRoute("/_authenticated/admin/sick-notes/new")({
  validateSearch: z.object({ reissue: z.string().uuid().optional() }),
  head: () => ({ meta: [
    { title: "Create Sick Note | N S Physiotherapy" },
    { name: "description", content: "Create a private medical certificate at N S Physiotherapy." },
    { property: "og:title", content: "Create Sick Note | N S Physiotherapy" },
    { property: "og:description", content: "Create a private medical certificate at N S Physiotherapy." },
    { property: "og:type", content: "website" },
    { name: "twitter:card", content: "summary" },
    { name: "robots", content: "noindex" },
  ] }),
  component: CreatePage,
});

const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = {
  patient_name: "",
  patient_id_number: "",
  patient_dob: "",
  patient_phone: "",
  patient_email: "",
  consultation_date: today(),
  practitioner_user_id: null as string | null,
  practitioner_name: "",
  registration_number: "",
  practice_name: P.name,
  practice_address: P.address,
  practitioner_contact: `${P.phone} · ${P.email}`,
  leave_type: "work" as LeaveType,
  leave_type_other: "",
  condition_nature: "" as ConditionNature | "",
  family_member_name: "",
  family_attendance_confirmed: false,
  leave_start: today(),
  leave_end: today(),
  return_date: suggestedReturnDate(today()),
  clinical_info: "",
};

function CreatePage() {
  const { reissue } = Route.useSearch();
  const qc = useQueryClient();
  const accessFn = useServerFn(getMyAccess);
  const practFn = useServerFn(listPractitioners);
  const getFn = useServerFn(getSickNote);
  const issueFn = useServerFn(issueSickNote);
  const actions = useSickNoteActions();

  const [form, setForm] = useState({ ...EMPTY });
  const [returnTouched, setReturnTouched] = useState(false);
  const [assessed, setAssessed] = useState(false);
  const [useSig, setUseSig] = useState(false);
  const [reason, setReason] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [issuing, setIssuing] = useState(false);
  const [issued, setIssued] = useState<SickNote | null>(null);
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }));

  const { data: access } = useQuery({ queryKey: ["sickNoteAccess"], queryFn: () => accessFn() });
  const { data: practitioners = [] } = useQuery({ queryKey: ["practitioners"], queryFn: () => practFn() });
  const { data: original } = useQuery({
    queryKey: ["sickNote", reissue],
    queryFn: () => getFn({ data: { id: reissue! } }),
    enabled: !!reissue,
  });
  const me = practitioners.find((p: any) => p.is_me);
  const selectedPract = practitioners.find((p: any) => p.user_id === form.practitioner_user_id);
  const canSign = !!selectedPract?.is_me && !!selectedPract?.signature_png;

  const applyPract = (p: any) =>
    set({
      practitioner_user_id: p.user_id,
      practitioner_name: p.full_name,
      registration_number: p.registration_number,
      practice_name: p.practice_name,
      practice_address: p.practice_address,
      practitioner_contact: [p.phone, p.email].filter(Boolean).join(" · "),
    });

  // Auto-fill from the logged-in practitioner's profile (new notes only).
  useEffect(() => {
    if (!reissue && me && !form.practitioner_name) applyPract(me);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me, reissue]);

  // Prefill from original when reissuing.
  useEffect(() => {
    if (!original) return;
    const n = original.note as SickNote;
    setForm({
      patient_name: n.patient_name,
      patient_id_number: n.patient_id_number ?? "",
      patient_dob: n.patient_dob ?? "",
      patient_phone: n.patient_phone ?? "",
      patient_email: n.patient_email ?? "",
      consultation_date: n.consultation_date,
      practitioner_user_id: n.practitioner_user_id,
      practitioner_name: n.practitioner_name,
      registration_number: n.registration_number,
      practice_name: n.practice_name,
      practice_address: n.practice_address,
      practitioner_contact: n.practitioner_contact ?? "",
      leave_type: n.leave_type,
      leave_type_other: n.leave_type_other ?? "",
      condition_nature: n.condition_nature ?? "",
      family_member_name: n.family_member_name ?? "",
      family_attendance_confirmed: n.family_attendance_confirmed ?? false,
      leave_start: n.leave_start,
      leave_end: n.leave_end,
      return_date: n.return_date,
      clinical_info: n.clinical_info ?? "",
    });
    setReturnTouched(true);
  }, [original]);

  // Suggest a return date until the practitioner edits it themselves.
  useEffect(() => {
    if (!returnTouched) set({ return_date: suggestedReturnDate(form.leave_end) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.leave_end, returnTouched]);

  const days = leaveDays(form.leave_start, form.leave_end);
  const dateError = validateSickNoteDates(form);
  const problems = useMemo(() => {
    const p: string[] = [];
    if (!form.patient_name.trim()) p.push("Enter the patient's full name.");
    if (!form.practitioner_name.trim() || !form.registration_number.trim()) p.push("Enter the practitioner's name and registration number.");
    if (!form.practice_name.trim() || !form.practice_address.trim()) p.push("Enter the practice name and address.");
    if (form.leave_type === "other" && !form.leave_type_other.trim()) p.push("Describe the leave type.");
    if (!form.condition_nature) p.push("Select the nature of the condition.");
    if (form.leave_type === "family_responsibility") {
      if (!form.family_member_name.trim()) p.push("Enter the accompanying family member's full name.");
      if (!form.family_attendance_confirmed) p.push("Confirm the family member accompanied the patient.");
    }
    if (dateError) p.push(dateError);
    if (!assessed) p.push("Confirm the patient was assessed/consulted by the practitioner.");
    if (reissue && !reason.trim()) p.push("Give a reason for reissuing.");
    return p;
  }, [form, dateError, assessed, reissue, reason]);

  const preview = {
    ...form,
    leave_days: days ?? undefined,
    signature_png: useSig && canSign ? selectedPract!.signature_png : null,
  } as Partial<SickNote> & { leave_type: LeaveType };

  async function issue() {
    setIssuing(true);
    try {
      const note = await issueFn({
        data: {
          ...form,
          patient_dob: form.patient_dob || null,
          assessed_confirmed: true,
          condition_nature: form.condition_nature as ConditionNature,
          apply_stored_signature: useSig && canSign,
          supersedes_id: reissue ?? null,
          reissue_reason: reason,
        },
      });
      setIssued(note as SickNote);
      qc.invalidateQueries({ queryKey: ["sickNotes"] });
      toast.success("Sick note generated successfully");
      window.scrollTo({ top: 0 });
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setIssuing(false);
      setConfirmOpen(false);
    }
  }

  if (access && !access.perms.includes(reissue ? "sick_notes.reissue" : "sick_notes.create")) {
    return <p className="text-sm text-muted-foreground">You don't have permission to {reissue ? "reissue" : "create"} sick notes.</p>;
  }

  if (issued) {
    return (
      <div className="mx-auto max-w-xl space-y-4">
        <Card>
          <CardContent className="space-y-4 p-6 text-center">
            <CheckCircle2 className="mx-auto h-10 w-10 text-primary" />
            <h2 className="text-xl font-semibold">Sick note generated successfully</h2>
            <p className="text-sm">Certificate Number: <span className="font-mono font-semibold">{issued.certificate_number}</span></p>
            <div className="flex flex-wrap justify-center gap-2">
              <Button onClick={() => actions.download(issued)}><FileDown className="mr-2 h-4 w-4" />Download PDF</Button>
              <Button variant="outline" onClick={() => actions.print(issued)}><Printer className="mr-2 h-4 w-4" />Print</Button>
              <Button asChild variant="outline"><Link to="/admin/sick-notes/$id" params={{ id: issued.id }}><Eye className="mr-2 h-4 w-4" />View Certificate</Link></Button>
              {issued.patient_email && <Button variant="outline" onClick={() => actions.email(issued)}><Mail className="mr-2 h-4 w-4" />Email patient</Button>}
              <Button
                variant="secondary"
                onClick={() => {
                  setIssued(null);
                  setForm({ ...EMPTY });
                  setAssessed(false);
                  setUseSig(false);
                  setReturnTouched(false);
                  if (me) applyPract(me);
                }}
                asChild={!!reissue}
              >
                {reissue ? <Link to="/admin/sick-notes/new"><Plus className="mr-2 h-4 w-4" />Create Another</Link> : <><Plus className="mr-2 h-4 w-4" />Create Another</>}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const Req = () => <span className="text-destructive">*</span>;
  const F = ({ label, req, children, span }: { label: string; req?: boolean; children: React.ReactNode; span?: boolean }) => (
    <div className={`space-y-1 ${span ? "sm:col-span-2" : ""}`}>
      <Label className="text-xs text-muted-foreground">{label} {req && <Req />}</Label>
      {children}
    </div>
  );

  return (
    <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,640px)]">
      <div className="space-y-4">
        {reissue && original && (
          <div className="rounded-md border bg-muted/50 p-3 text-sm">
            Reissuing <span className="font-mono">{(original.note as SickNote).certificate_number}</span>. A new certificate number will be created and the original kept as superseded.
            <div className="mt-2 space-y-1">
              <Label className="text-xs text-muted-foreground">Reason for reissue <Req /></Label>
              <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. corrected ID number" />
            </div>
          </div>
        )}

        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Patient information</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <PatientSearch onPick={(p) => set({ patient_name: p.name, patient_email: p.email, patient_phone: p.phone, patient_id_number: p.id_number, patient_dob: p.dob })} />
            <div className="grid gap-3 sm:grid-cols-2">
              {F({ label: "Full name", req: true, span: true, children: <Input value={form.patient_name} onChange={(e) => set({ patient_name: e.target.value })} /> })}
              {F({ label: "ID / Passport number", children: <Input value={form.patient_id_number} onChange={(e) => set({ patient_id_number: e.target.value })} /> })}
              {F({ label: "Date of birth", children: <Input type="date" value={form.patient_dob} onChange={(e) => set({ patient_dob: e.target.value })} /> })}
              {F({ label: "Contact number", children: <Input type="tel" value={form.patient_phone} onChange={(e) => set({ patient_phone: e.target.value })} /> })}
              {F({ label: "Email address", children: <Input type="email" value={form.patient_email} onChange={(e) => set({ patient_email: e.target.value })} /> })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Consultation information</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {practitioners.length > 0 && (
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Practitioner profile</Label>
                <select
                  className="h-9 w-full rounded-md border bg-background px-2 text-sm"
                  value={form.practitioner_user_id ?? ""}
                  onChange={(e) => {
                    const p = practitioners.find((x: any) => x.user_id === e.target.value);
                    if (p) applyPract(p);
                    else set({ practitioner_user_id: null });
                    setUseSig(false);
                  }}
                >
                  <option value="">Enter manually</option>
                  {practitioners.map((p: any) => <option key={p.user_id} value={p.user_id}>{p.full_name}{p.is_me ? " (you)" : ""}</option>)}
                </select>
              </div>
            )}
            {!me && (
              <p className="text-xs text-muted-foreground">
                Tip: save your details and signature on the <Link to="/admin/sick-notes/practitioner" className="underline">Practitioner profile</Link> page to fill these in automatically.
              </p>
            )}
            <div className="grid gap-3 sm:grid-cols-2">
              {F({ label: "Consultation date", req: true, children: <Input type="date" value={form.consultation_date} onChange={(e) => set({ consultation_date: e.target.value })} /> })}
              {F({ label: "Practitioner name", req: true, children: <Input value={form.practitioner_name} onChange={(e) => set({ practitioner_name: e.target.value })} /> })}
              {F({ label: "Professional registration number", req: true, children: <Input value={form.registration_number} onChange={(e) => set({ registration_number: e.target.value })} placeholder={P.hpcsa} /> })}
              {F({ label: "Practice name", req: true, children: <Input value={form.practice_name} onChange={(e) => set({ practice_name: e.target.value })} /> })}
              {F({ label: "Practice address", req: true, span: true, children: <Input value={form.practice_address} onChange={(e) => set({ practice_address: e.target.value })} /> })}
              {F({ label: "Practitioner contact details", span: true, children: <Input value={form.practitioner_contact} onChange={(e) => set({ practitioner_contact: e.target.value })} /> })}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Medical leave information</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              {F({
                label: "Type",
                req: true,
                 span: true,
                children: (
                  <select className="h-9 w-full rounded-md border bg-background px-2 text-sm" value={form.leave_type} onChange={(e) => set({ leave_type: e.target.value as LeaveType })}>
                    <option value="work">Work</option>
                    <option value="school">School</option>
                    <option value="work_from_home">Work from home</option>
                    <option value="light_duty">Light duty</option>
                    <option value="family_responsibility">Family responsibility</option>
                    <option value="other">Other</option>
                  </select>
                ),
              })}
              {form.leave_type === "other"
                ? F({ label: "Describe (e.g. sport, university)", req: true, children: <Input value={form.leave_type_other} onChange={(e) => set({ leave_type_other: e.target.value })} /> })
                : <div />}
               {F({
                 label: "Nature of condition", req: true, span: true,
                 children: (
                   <select className="h-9 w-full rounded-md border bg-background px-2 text-sm" value={form.condition_nature} onChange={(e) => set({ condition_nature: e.target.value as ConditionNature | "" })}>
                     <option value="">Select a category</option>
                     {CONDITION_NATURES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
                   </select>
                 ),
               })}
               {form.leave_type === "family_responsibility" && F({
                 label: "Accompanying family member's full name", req: true, span: true,
                 children: <Input value={form.family_member_name} onChange={(e) => set({ family_member_name: e.target.value })} />,
               })}
               {form.leave_type === "family_responsibility" && (
                 <label className="flex items-start gap-2 sm:col-span-2">
                   <Checkbox checked={form.family_attendance_confirmed} onCheckedChange={(v) => set({ family_attendance_confirmed: v === true })} />
                   <span className="text-sm">The named family member accompanied the patient <Req /></span>
                 </label>
               )}
              {F({ label: "First day of leave", req: true, children: <Input type="date" value={form.leave_start} onChange={(e) => set({ leave_start: e.target.value })} /> })}
              {F({ label: "Last day of leave", req: true, children: <Input type="date" value={form.leave_end} min={form.leave_start} onChange={(e) => set({ leave_end: e.target.value })} /> })}
              {F({
                label: "Expected return date",
                req: true,
                children: (
                  <Input type="date" value={form.return_date} onChange={(e) => { setReturnTouched(true); set({ return_date: e.target.value }); }} />
                ),
              })}
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Number of leave days</Label>
                <p className="flex h-9 items-center rounded-md border bg-muted/40 px-3 text-sm font-medium">{days ? `${days} day${days === 1 ? "" : "s"}` : "—"}</p>
              </div>
            </div>
            {!returnTouched && form.return_date && (
              <p className="text-xs text-muted-foreground">Return date suggested as the day after leave ends ({formatInvoiceDate(form.return_date)}). Change it if needed.</p>
            )}
            {dateError && <p className="text-sm text-destructive">{dateError}</p>}
            {F({
              label: "Reason / clinical information (optional — only what is appropriate and authorised)",
              children: <Textarea rows={3} value={form.clinical_info} onChange={(e) => set({ clinical_info: e.target.value })} placeholder="Leave blank if the patient has not consented to share clinical details." />,
            })}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-base">Signature & confirmation</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            {canSign ? (
              <label className="flex items-start gap-2">
                <Checkbox checked={useSig} onCheckedChange={(v) => setUseSig(!!v)} />
                <span>Apply my stored electronic signature</span>
              </label>
            ) : (
              <p className="text-xs text-muted-foreground">
                {selectedPract?.is_me
                  ? "You have no stored signature yet — the certificate will have a signature line for signing by hand."
                  : "Only the practitioner can apply their own electronic signature. The certificate will have a signature line for signing by hand."}
              </p>
            )}
            <label className="flex items-start gap-2 rounded-md border p-3">
              <Checkbox checked={assessed} onCheckedChange={(v) => setAssessed(!!v)} />
              <span>Patient was assessed/consulted by the practitioner <Req /></span>
            </label>
            {problems.length > 0 && (
              <ul className="list-disc space-y-0.5 pl-5 text-xs text-muted-foreground">
                {problems.map((p) => <li key={p}>{p}</li>)}
              </ul>
            )}
            <Button className="w-full" disabled={problems.length > 0 || issuing} onClick={() => setConfirmOpen(true)}>
              {issuing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileDown className="mr-2 h-4 w-4" />}
              {reissue ? "Reissue certificate" : "Generate PDF"}
            </Button>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-2 xl:sticky xl:top-20 xl:self-start">
        <p className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Live preview</p>
        <SickNoteDocument note={preview} draft />
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Issue this certificate?</AlertDialogTitle>
            <AlertDialogDescription>
              A unique certificate number will be assigned for {form.patient_name} ({days} day{days === 1 ? "" : "s"}). Once issued it can't be edited — corrections need a reissue.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={issuing}>Go back</AlertDialogCancel>
            <AlertDialogAction disabled={issuing} onClick={(e) => { e.preventDefault(); issue(); }}>
              {issuing ? "Issuing..." : "Issue certificate"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function PatientSearch({ onPick }: { onPick: (p: { name: string; email: string; phone: string; id_number: string; dob: string }) => void }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const fn = useServerFn(searchSickNotePatients);
  const { data = [], isFetching } = useQuery({
    queryKey: ["sickNotePatientSearch", q],
    queryFn: () => fn({ data: { q } }),
    enabled: q.trim().length >= 2,
    staleTime: 30_000,
  });
  return (
    <div className="relative">
      <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
      <Input className="pl-8" value={q} onFocus={() => setOpen(true)} onChange={(e) => { setQ(e.target.value); setOpen(true); }} placeholder="Search existing patients by name, email, phone or ID" />
      {isFetching && <Loader2 className="absolute right-2 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />}
      {open && q.trim().length >= 2 && !isFetching && (
        <div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-md border bg-popover text-popover-foreground shadow-md">
          {data.length === 0 ? (
            <p className="p-3 text-xs text-muted-foreground">No matching patients — fill in the details below.</p>
          ) : (
            data.map((p) => (
              <button
                key={p.email + p.name}
                type="button"
                className="block w-full px-3 py-2 text-left text-sm hover:bg-accent"
                onClick={() => { onPick(p); setOpen(false); setQ(""); }}
              >
                <span className="font-medium">{p.name}</span>
                <span className="block text-xs text-muted-foreground">{[p.email, p.phone].filter(Boolean).join(" · ")}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
